import { z } from "zod";
import { assertPaise } from "./integer";

/**
 * UPI payment links (SF-12, OP-12). See "UPI details" in ordering-billing-gst.md:
 *
 *   upi://pay?pa=<vpa>&pn=<payee name>&am=<amount>&cu=INR&tn=<bill no>&tr=<bill id short>
 *
 * LuckyMenu never touches the money: the link sends the diner's UPI app to the
 * restaurant's own UPI ID with the amount filled in. Staff confirm receipt.
 */

/**
 * A UPI ID: a handle, `@`, and the provider, such as `spicegarden@okhdfcbank`.
 * Letters, digits, `.`, `-` and `_` in the handle, letters and digits in the provider.
 */
export const upiVpaSchema = z
	.string()
	.trim()
	.regex(/^[A-Za-z0-9._-]{2,256}@[A-Za-z0-9]{2,64}$/, "A UPI ID such as name@bank");

export interface UpiPaymentRequest {
	/** The restaurant's UPI ID (`upi_vpa`). */
	vpa: string;
	/** Shown by the diner's UPI app (`upi_payee_name`). */
	payeeName: string;
	/** More than zero. */
	amountPaise: number;
	/** The bill number, shown to the payer as the note. Omitted before a bill exists. */
	billNo?: string | null;
	/** A short reference for the payment, see `upiReference`. */
	reference?: string | null;
}

/** Paise as the UPI amount format: rupees with exactly two decimals, "540.00". */
export function upiAmount(paise: number): string {
	assertPaise(paise, "UPI amount");
	return `${Math.trunc(paise / 100)}.${String(paise % 100).padStart(2, "0")}`;
}

/**
 * The "bill id short" of the link: the last 12 hex digits of the bill's
 * UUIDv7, which are random, where the leading digits are a timestamp shared by
 * bills made close together.
 */
export function upiReference(billId: string): string {
	const hex = billId.replaceAll("-", "").toLowerCase();
	if (!/^[0-9a-f]{32}$/.test(hex)) throw new RangeError(`Not a UUID: ${billId}`);
	return hex.slice(-12);
}

/**
 * Percent-encodes a value for the link. Everything except letters, digits and
 * `-._~` is encoded, spaces as `%20` (never `+`, which some UPI apps show as a
 * plus sign), and non-ASCII names as UTF-8.
 */
export function upiEncode(value: string): string {
	return encodeURIComponent(value).replace(
		/[!'()*]/g,
		(c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
	);
}

export function upiLink(request: UpiPaymentRequest): string {
	const vpa = upiVpaSchema.safeParse(request.vpa);
	if (!vpa.success) throw new RangeError(`Not a UPI ID: ${request.vpa}`);
	const payeeName = request.payeeName.trim();
	if (payeeName === "") throw new RangeError("The payee name is empty");
	if (request.amountPaise <= 0) throw new RangeError("The amount must be more than zero");

	// The UPI ID is safe as it is: the schema allows only characters that need
	// no encoding, and `@` stays readable for apps that do not decode `%40`.
	const params: [string, string][] = [
		["pa", vpa.data],
		["pn", upiEncode(payeeName)],
		["am", upiAmount(request.amountPaise)],
		["cu", "INR"],
	];
	if (request.billNo) params.push(["tn", upiEncode(request.billNo)]);
	if (request.reference) params.push(["tr", upiEncode(request.reference)]);
	return `upi://pay?${params.map(([k, v]) => `${k}=${v}`).join("&")}`;
}
